import type {
  AvailabilityResponse,
  BookingRequest,
  BookingResponse,
  ContactFormData,
  GHLOrderResponse,
  GHLOrderStatusResponse,
  PaymentIntentResponse,
  WidgetConfig,
} from '@yebo/shared';

// ── Prepaid event-ticket API shapes ──────────────────────────────────────────
// Money is integer cents; all prices are SERVER-AUTHORITATIVE — the widget never
// sends a client-chosen amount, it only asks the server to quote/charge.

/** Live availability returned by /ticket/quote (and echoed elsewhere). */
/** #652 — one line of a mixed-tier basket, as sent to the server. Quantities
 *  only; the server resolves every price from stored config. */
export interface TicketLine {
  tierId: string;
  /** Seats on this tier, children included. */
  qty: number;
  /** How many of those seats are children. */
  kidsQty: number;
}

/** #652 — one priced line, as the server returns it. */
export interface TicketQuoteLine extends TicketLine {
  tierLabel?: string;
  unitPriceCents: number;
  kidsUnitPriceCents: number;
  subtotalCents: number;
}

export interface TicketAvailability {
  capacity: number;
  coversSold: number;
  remaining: number;
  soldOut: boolean;
}

/**
 * #816 — an ACCEPTED promo code, exactly as the server describes it.
 *
 * Every number here is server-computed. `ticketsRemaining` is OPTIONAL and its
 * absence means UNKNOWN — never "unlimited". The widget renders a pool hint
 * only when the server actually sent a number.
 */
export interface TicketCouponApplied {
  code: string;
  percentOff: number;
  discountCents: number;
  ticketsRemaining?: number;
  /** #954 — how the server priced it ('percent' | 'pairs'). A label input only. */
  kind?: string;
  /** #954 — the deal's code-free name ("Buy 1 Get 1 Free", "10% off"). */
  label?: string;
}

/**
 * #954 — a live auto deal (e.g. Buy 1 Get 1 Free) that did NOT apply to this
 * basket. Every word is the server's: `message` is the guest-facing sentence
 * (a nudge, the same-type explainer, or the honest "fully redeemed" note), and
 * it is information, never an error. `reason` is for branching only.
 */
export interface TicketDealNotice {
  label: string;
  reason: string;
  nudge?: { tierId: string; tierLabel?: string; add: number };
  message: string;
}

/**
 * #816 — a REFUSED promo code, parsed out of a 400/409 error body.
 *
 * 400 = "this code will never work" (COUPON_INVALID / _INACTIVE / _EXPIRED),
 * 409 = "the code is real but the pool moved" (COUPON_EXHAUSTED /
 * _ALREADY_USED). The widget deliberately branches on NEITHER: it renders the
 * server's own sentence and re-quotes at full price. The status is kept only so
 * a caller can log it.
 */
export interface TicketCouponRejection {
  status: number;
  /** The server's guest-facing sentence. */
  message: string;
  /** The machine code the server sent, when it sent one. */
  code?: string;
  ticketsRemaining?: number;
  /** #814 B3 — a 503 `coupon_unavailable`: the code may well be fine, the
   *  server just could not check it. The guest is invited to try again rather
   *  than told the code is dead. */
  retryable?: boolean;
}

/**
 * Recognise a coupon refusal in an error (or a 200-with-`ok:false`) body.
 * Returns null for anything that is not `reason: 'coupon_rejected'`, so every
 * other failure keeps its existing handling. Never throws.
 */
export function parseCouponRejection(err: unknown): TicketCouponRejection | null {
  if (!(err instanceof WidgetApiError)) return null;
  let parsed: any;
  try {
    parsed = JSON.parse(err.body);
  } catch {
    return null;
  }
  // #814 G3 B3 — the server distinguishes "this code will never work"
  // (`coupon_rejected`, 400/409) from "we could not CHECK it just now"
  // (`coupon_unavailable`, 503, `retryable: true`). Both are coupon answers and
  // both must leave a purchasable full-price order; only the copy differs, and
  // the server supplies it. Anything else is a real quote failure.
  if (!parsed || (parsed.reason !== 'coupon_rejected' && parsed.reason !== 'coupon_unavailable')) {
    return null;
  }
  return {
    status: err.status,
    message: typeof parsed.error === 'string' && parsed.error
      ? parsed.error
      : parsed.reason === 'coupon_unavailable'
        ? 'We could not check that promo code just now. Please try again.'
        : 'That promo code could not be applied.',
    /** #814 B3 — true when the code may well be fine and a retry is worth it. */
    retryable: parsed.retryable === true,
    code: typeof parsed.code === 'string' ? parsed.code : undefined,
    ticketsRemaining: typeof parsed.ticketsRemaining === 'number' ? parsed.ticketsRemaining : undefined,
  };
}

/** 200 response from POST /ticket/quote. */
export interface TicketQuoteResponse {
  ok: true;
  eventId: string;
  eventName: string;
  /** #652 — present when the request sent `ticketLines`. The per-line
   *  breakdown the guest sees; `totalCents` is still the one authoritative
   *  number and the only one that must match what is charged. */
  lines?: TicketQuoteLine[];
  /** Derived from Σ qty when lines were sent. */
  partySize?: number;
  /** Optional because a mixed basket has no single unit price. */
  unitPriceCents?: number;
  kidsUnitPriceCents?: number;
  kidsCount?: number;
  /** #816 — pre-discount ticket total. Present when a coupon was applied. */
  subtotalCents?: number;
  /** #816 — the money taken off, server-computed. */
  discountCents?: number;
  /** The authoritative charge — already DISCOUNTED when a coupon applied. */
  totalCents: number;
  currency: string;
  tierId?: string;
  tierLabel?: string;
  /** #816 — present only when a promo code was accepted (typed, or #954 auto). */
  coupon?: TicketCouponApplied;
  /** #954 — true when NO code was typed and the event's auto deal applied:
   *  `coupon` then describes the deal, and its code is never shown or sent. */
  autoApplied?: boolean;
  /** #954 — a live auto deal that did not fit this basket (full price). */
  deal?: TicketDealNotice;
  availability: TicketAvailability;
}

/** 400 response from POST /ticket/quote. */
export interface TicketQuoteError {
  ok: false;
  error: string;
  reason?: string;
}

/** 200 response from POST /ticket/checkout — holds the seat + returns a payment handle. */
export interface TicketCheckoutResponse {
  reservationId: string;
  date: string;                 // YYYY-MM-DD the seat is held for
  confirmationNumber: string;
  amountCents: number;
  currency: string;
  provider: string;             // 'stripe' | 'ghl' | …
  clientSecret?: string;        // Stripe Payment Element
  authorizationUrl?: string;    // redirect gateway
  eventName: string;
  tierLabel?: string;
  /** #816 — the coupon breakdown echoed back by checkout. */
  subtotalCents?: number;
  discountCents?: number;
  couponCode?: string;
  /** #954 — the discount was the event's auto deal, not a typed code. */
  autoApplied?: boolean;
  /**
   * #816 — a 100%-comped order. The server settles it itself and answers
   * `paid: true` / `status: 'confirmed'` / `depositStatus: 'waived'` with NO
   * payment handle. The widget must treat that as a COMPLETED purchase and
   * never sit waiting for a gateway that will never be called.
   */
  paid?: boolean;
  status?: string;
  depositStatus?: string;
  /** #652 — set when the server replayed a prior attempt on the same
   *  idempotency key. A replay carries NO payment handle, so the client must
   *  not advance to the payment step on it. */
  idempotentReplay?: boolean;
}

/** 200 response from POST /ticket/verify. */
export interface TicketVerifyResponse {
  success: boolean;
  status: string;               // 'confirmed' on success
  confirmationNumber?: string;
  gatewayStatus?: string;
  /** Money arrived after the hold lapsed: the seat is gone and a refund is owed.
   *  The guest must NOT be told to pay again. */
  refundDue?: boolean;
  /** Honest server-side reason when success is false and it is not a plain not-yet-paid poll. */
  reason?: string;
}

// ── Enriched availability (R10.4) ─────────────────────────────────────────────
// The shared `AvailabilityResponse` is the lead-owned contract (View 5 · R5.1
// adds the enrichment server-side). The widget reads these OPTIONAL fields
// defensively: every one may be absent until the backend ships enrichment, and
// the empty-state recovery degrades to a plain "try another date" affordance.
// This local type never widens or replaces the shared one — it only annotates
// the extra fields the widget knows how to consume.

/** An alternate same-day time the guest could take instead. */
export interface AvailabilityAlternateTime {
  start: string;
  end: string;
  available: boolean;
  sectionLabel?: string;
}

/** An online section with its own availability, offered as an alternate. */
export interface AvailabilityAlternateSection {
  sectionId: string;
  sectionLabel: string;
  nextAvailable?: string; // ISO 8601 of the section's next bookable slot
}

/** Optional enrichment layered onto the shared AvailabilityResponse. */
export interface AvailabilityEnrichment {
  /** Typed reason the date is empty: 'fully_booked' | 'closed' | 'past' | … */
  reason?: string;
  /** Alternate same-day times (possibly in other online sections). */
  alternateTimes?: AvailabilityAlternateTime[];
  /** Other online sections that still have availability. */
  alternateSections?: AvailabilityAlternateSection[];
  /** The next date that has availability — drives "try another date". */
  nextAvailableDate?: string;
}

export type EnrichedAvailabilityResponse = AvailabilityResponse & AvailabilityEnrichment;

// ── Recurring experiences — lazy occurrence list (#130) ───────────────────────
// A recurring series surfaces as ONE PublicEvent card; its bookable occurrences
// (with live remaining covers) load lazily when the date step opens. The `occId`
// is OPAQUE to the widget — it is read from this response and sent back verbatim
// to quote/checkout/book, never constructed or `#`-split client-side.

/** A single bookable occurrence of a recurring series. */
export interface ExperienceOccurrence {
  occId: string;                // opaque `${seriesId}#${date}` — sent back verbatim
  date: string;                 // 'YYYY-MM-DD'
  coversRemaining: number;      // live remaining covers for this sitting
  soldOut?: boolean;
}

/** 200 response from GET /api/widget/experiences/:locationId/:seriesId/occurrences. */
export interface ExperienceOccurrencesResponse {
  occurrences: ExperienceOccurrence[];
}

/** Request body for POST /api/widget/notify (R10.6). */
export interface JoinNotifyRequest {
  locationId: string;
  date: string;                 // YYYY-MM-DD the guest wants
  partySize: number;
  contact: ContactFormData;
  preferredTime?: string;       // "HH:mm" preferred window start
  notes?: string;
  consent?: BookingRequest['consent'];
}

/** 200 response from POST /api/widget/notify. */
export interface JoinNotifyResponse {
  ok: boolean;
  waitlistId?: string;
}

// ── Guest deposit checkout (#480 · design §2) ─────────────────────────────────
// Every amount is SERVER-DERIVED. The request is a BookingRequest-shaped body
// plus a REQUIRED idempotencyKey; the server rejects any client-supplied
// payment field with 400 `client_supplied_payment_field`, so these types
// deliberately have no room for one.

/** Request body for POST /api/widget/deposit/checkout (§2.1). */
export type DepositCheckoutRequest = Omit<
  BookingRequest,
  'paymentIntentId' | 'ghlOrderId'
> & {
  /** REQUIRED here (unlike /book, where it is optional). */
  idempotencyKey: string;
};

/** 200 response from POST /api/widget/deposit/checkout (§2.1 A / B / C). */
export interface DepositCheckoutResponse {
  /** false ⇒ no reservation was created; the widget runs today's /book path. */
  depositRequired: boolean;
  reservationId?: string;
  date?: string;                 // YYYY-MM-DD the seat is held for
  confirmationNumber?: string;
  expiresAt?: string;            // ISO hold deadline — displayed verbatim
  amountCents?: number;
  currency?: string;
  depositType?: string;
  captureMode?: 'charge' | 'hold';
  depositStatus?: string;
  /** DIAGNOSTIC ONLY — never a render selector (D6). */
  provider?: string;
  /** Exactly one of these two is non-null when `depositRequired` is true. */
  clientSecret?: string | null;
  authorizationUrl?: string | null;
  /** The server's honest note about what its rail does with the money. */
  policyNote?: string | null;
  autoReleaseAt?: string | null;
  /** Response C — the idempotency key was already claimed. */
  idempotentReplay?: boolean;
  /** Response C sub-case — already paid/authorized; go straight to verify. */
  settled?: boolean;
}

/** 200 response from POST /api/widget/deposit/verify (§2.2). */
export interface DepositVerifyResponse {
  success: boolean;
  depositStatus?: string;
  gatewayStatus?: string;
  reservationStatus?: string;    // 'confirmed' once settled
  confirmationNumber?: string;
  expiresAt?: string;            // present while still pending
}

export class WidgetApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`API error ${status}: ${body}`);
    this.name = 'WidgetApiError';
  }

  /**
   * The server's machine-readable `code` from the error body, if it sent one.
   *
   * #480 G3 — the status ALONE is not enough to recover from a deposit
   * refusal: `/deposit/checkout` answers 409 for a genuine capacity loss
   * (`slot_unavailable`) AND for two replay refusals (`replay_unresolved`,
   * `replay_lookup_failed`) that have nothing to do with the slot and need a
   * completely different recovery. Parsed lazily and defensively — a non-JSON
   * or code-less body is simply `undefined`, never a throw.
   */
  get code(): string | undefined {
    return parseErrorCode(this.body);
  }
}

/** Pull `code` out of a JSON error body. Never throws; `undefined` when absent. */
export function parseErrorCode(body: unknown): string | undefined {
  if (typeof body !== 'string' || !body) return undefined;
  try {
    const parsed = JSON.parse(body) as { code?: unknown };
    return typeof parsed?.code === 'string' ? parsed.code : undefined;
  } catch {
    return undefined;
  }
}

export class WidgetApiClient {
  private baseUrl: string;
  private widgetToken: string | null = null;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || '/api/widget';
  }

  setToken(token: string): void {
    this.widgetToken = token;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    if (this.widgetToken) {
      headers['X-Widget-Token'] = this.widgetToken;
    }

    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new WidgetApiError(res.status, text);
    }

    return res.json();
  }

  async getConfig(locationId: string): Promise<WidgetConfig> {
    return this.request<WidgetConfig>('GET', `/config/${encodeURIComponent(locationId)}`);
  }

  async getAvailability(params: {
    locationId: string;
    calendarId: string;
    date: string;
    tz: string;
    partySize?: number;
  }): Promise<EnrichedAvailabilityResponse> {
    const qs = new URLSearchParams();
    qs.set('locationId', params.locationId);
    qs.set('calendarId', params.calendarId);
    qs.set('date', params.date);
    qs.set('tz', params.tz);
    if (params.partySize) qs.set('partySize', String(params.partySize));
    // Server may return the bare shared shape OR the enriched one; the widget
    // reads the enrichment fields defensively (all optional).
    return this.request<EnrichedAvailabilityResponse>('GET', `/availability?${qs.toString()}`);
  }

  /**
   * R10.6 — Join Notify. Posts the guest onto the cancellation-recovery demand
   * list (a `notify`-type WaitlistEntry, server-side). HMAC-token gated like
   * every non-/config widget call.
   */
  /**
   * #651 — the event's menu/info PDF.
   *
   * Returns a SHORT-LIVED presigned S3 URL rather than the file, and the
   * caller navigates to that URL. It is deliberately not a plain link to the
   * API: the widget token rides the `X-Widget-Token` header, which only a
   * `fetch` can set, and moving it into a query string would put a 24-hour
   * tenant credential into browser history and every access log.
   *
   * The returned URL expires in ~2 minutes, so fetch it on click — never at
   * render time.
   */
  async getEventMenuPdfUrl(eventId: string): Promise<string> {
    const res = await this.request<{ url: string }>(
      'GET',
      `/events/${encodeURIComponent(eventId)}/menu-pdf`,
    );
    return res.url;
  }

  async joinNotify(data: JoinNotifyRequest): Promise<JoinNotifyResponse> {
    return this.request<JoinNotifyResponse>('POST', '/notify', data);
  }

  async book(data: BookingRequest): Promise<BookingResponse> {
    return this.request<BookingResponse>('POST', '/book', data);
  }

  /** Legacy Stripe PaymentIntent */
  async createPaymentIntent(data: {
    locationId: string;
    amount: number;
    currency: string;
    // Booking context — sent so the server can re-validate the deposit amount
    // against the tenant's stored deposit config (#260/#286). Without these a
    // per-person deposit cannot be resolved server-side.
    partySize?: number;
    kidsCount?: number;
    date?: string;
    metadata?: Record<string, string>;
  }): Promise<PaymentIntentResponse> {
    return this.request<PaymentIntentResponse>('POST', '/create-payment-intent', data);
  }

  /** Create a GHL invoice/order for payment */
  async createGhlOrder(data: {
    locationId: string;
    amount: number;
    currency: string;
    contact: ContactFormData;
    description?: string;
  }): Promise<GHLOrderResponse & { contactId: string }> {
    return this.request<GHLOrderResponse & { contactId: string }>('POST', '/create-ghl-order', data);
  }

  // ── Guest deposits (#480) ──────────────────────────────────────────────────
  // `chargeDeposit` / POST /deposit/charge is RETIRED (design §2.3, ruling R4):
  // its only caller was the widget's fabricated `pending_<timestamp>` call, which
  // minted an orphan gateway intent on every attempt and swallowed its own
  // failures. Do not reintroduce it — /deposit/checkout is the only creation
  // path, and it is server-authoritative for every amount.

  /**
   * Decide + hold in one call. The server answers whether THIS booking owes a
   * deposit and, if so, creates the `pending` reservation and returns exactly
   * one payment handle (`clientSecret` XOR `authorizationUrl`).
   *
   * Thin transport: the body is sent exactly as the caller built it. The client
   * never adds a server-authoritative field (`amountCents`, `currency`,
   * `provider`, …) — those are 400 `client_supplied_payment_field` server-side
   * (DR-PAYMENTS-01), and the caller must not construct them either.
   */
  async depositCheckout(body: DepositCheckoutRequest): Promise<DepositCheckoutResponse> {
    return this.request<DepositCheckoutResponse>('POST', '/deposit/checkout', body);
  }

  /**
   * Settle + confirm. Polled by the widget (every 5 s while a hosted page is
   * open) and by the static return page (§8.2 backoff). A not-yet-paid poll is
   * a 200 with `success: false` — NOT an error.
   */
  async verifyDeposit(params: {
    locationId: string;
    reservationId: string;
    date: string;
  }): Promise<DepositVerifyResponse> {
    return this.request<DepositVerifyResponse>('POST', '/deposit/verify', params);
  }

  /**
   * #492 — tell the server the guest explicitly cancelled on the gateway's
   * page, so the unpaid hold releases NOW instead of waiting out the sweep.
   * Called once by the static cancelled page. `released:false` is a normal
   * outcome (`already_settled` / `already_released`), never an error.
   */
  async cancelDeposit(params: {
    locationId: string;
    reservationId: string;
    date: string;
  }): Promise<{
    released: boolean;
    // 'unverified' = the server could not prove no money moved (gateway
    // unreachable) and deliberately released nothing — the sweep backstop
    // still owns the hold. The page must NOT say the slot was released.
    reason?: 'already_settled' | 'already_released' | 'unverified';
    reservationStatus?: string;
    confirmationNumber?: string;
  }> {
    return this.request('POST', '/deposit/cancel', params);
  }

  /** Poll GHL order/invoice status */
  async getGhlOrderStatus(orderId: string, locationId: string): Promise<GHLOrderStatusResponse> {
    return this.request<GHLOrderStatusResponse>(
      'GET',
      `/ghl-order-status/${encodeURIComponent(orderId)}?locationId=${encodeURIComponent(locationId)}`,
    );
  }

  // ── Prepaid event tickets ──────────────────────────────────────────────────
  // The server is authoritative for price + availability; the widget sends only
  // the selection (event, tier, party size) and trusts the returned amounts.

  /**
   * Quote a ticketed-event purchase. Returns the authoritative unit/total price
   * and live availability, or a typed error on a 400 (e.g. party-size / tier
   * validation). Both branches resolve (the 400 is surfaced as `{ ok: false }`).
   */
  async quoteTicket(params: {
    locationId: string;
    eventId: string;
    partySize: number;
    tierId?: string;
    kidsCount?: number;
    /** #652 — a mixed-tier basket. When present the server DERIVES partySize
     *  from it and ignores the scalar fields. */
    ticketLines?: TicketLine[];
    /** #816 — a promo code. A STRING ONLY: the widget never sends an amount,
     *  a percentage or a discounted total — the server prices everything. */
    couponCode?: string;
  }): Promise<TicketQuoteResponse | TicketQuoteError> {
    return this.request<TicketQuoteResponse | TicketQuoteError>('POST', '/ticket/quote', params);
  }

  /**
   * Check out a ticketed event: HOLDS the seat and returns a payment handle
   * (`clientSecret` for the Stripe Payment Element, or `authorizationUrl` for a
   * redirect gateway) — identical shape to the existing deposit flow. A 409 means
   * the event sold out between quote and checkout (surfaced as WidgetApiError).
   */
  async checkoutTicket(params: {
    locationId: string;
    eventId: string;
    tierId?: string;
    partySize: number;
    kidsCount?: number;
    /** #652 — a mixed-tier basket; authoritative over partySize when sent. */
    ticketLines?: TicketLine[];
    /** #816 — the promo code the server already accepted at quote. String only. */
    couponCode?: string;
    /**
     * #954 — "the guest was SHOWN the auto deal". A tripwire only: the server
     * re-derives the deal from stored config and answers 409 AUTO_DEAL_CHANGED
     * if it would no longer give it. It can never grant or shape a discount.
     */
    autoApplied?: true;
    contact: ContactFormData;
    idempotencyKey?: string;
    consent?: BookingRequest['consent'];
  }): Promise<TicketCheckoutResponse> {
    return this.request<TicketCheckoutResponse>('POST', '/ticket/checkout', params);
  }

  /** Verify a ticketed purchase completed payment (post-Stripe/redirect). */
  async verifyTicket(params: {
    locationId: string;
    reservationId: string;
    date: string;
  }): Promise<TicketVerifyResponse> {
    return this.request<TicketVerifyResponse>('POST', '/ticket/verify', params);
  }

  /**
   * #130 — list the bookable occurrences of a recurring "standard experience"
   * series, with live remaining covers. Loaded lazily when the guest opens the
   * date step for a recurring card. The returned `occId` is opaque: the widget
   * sends it back unchanged to quote/checkout/book and never parses it. seriesId
   * rides in the URL for this READ; the booking mutations carry the occId in the
   * body (quote/checkout/book POST it) per the #130 invariant.
   */
  async listOccurrences(
    locationId: string,
    seriesId: string,
  ): Promise<ExperienceOccurrencesResponse> {
    return this.request<ExperienceOccurrencesResponse>(
      'GET',
      `/experiences/${encodeURIComponent(locationId)}/${encodeURIComponent(seriesId)}/occurrences`,
    );
  }
}

/** Singleton instance — base URL can be overridden via widget attribute */
export const apiClient = new WidgetApiClient();
